import { useEffect } from 'react';
import { batch } from '@tanstack/store';
import * as Y from 'yjs';

type ObservableForm = {
  setFieldValue: (fieldName: string, value: unknown) => void;
  insertFieldValue: (
    fieldName: string,
    index: number,
    value: unknown,
  ) => void | Promise<void>;
  removeFieldValue: (fieldName: string, index: number) => void | Promise<void>;
};

type FieldUpdate = {
  type: 'set';
  path: string;
  value: unknown;
};

type ArrayInsertUpdate = {
  type: 'insert';
  path: string;
  index: number;
  value: unknown;
};

type ArrayDeleteUpdate = {
  type: 'delete';
  path: string;
  index: number;
};

type FormUpdate = FieldUpdate | ArrayInsertUpdate | ArrayDeleteUpdate;

const getYjsValue = (value: unknown) => {
  if (
    value &&
    typeof value === 'object' &&
    'toJSON' in value &&
    typeof value.toJSON === 'function'
  ) {
    return value.toJSON();
  }

  return value;
};

const getFieldPath = (path: (string | number)[]) => {
  return path.reduce<string>((fieldPath, segment) => {
    if (typeof segment === 'number') return `${fieldPath}[${segment}]`;
    return fieldPath ? `${fieldPath}.${segment}` : segment;
  }, '');
};

const addMapUpdates = (
  updates: FormUpdate[],
  event: Y.YMapEvent<any>,
  valsMap: Y.Map<any>,
) => {
  event.keysChanged.forEach((key) => {
    const path = getFieldPath([...event.path, key]);
    const yjsValue = event.path.length
      ? event.target.get(key)
      : valsMap.get(key);

    updates.push({ type: 'set', path, value: getYjsValue(yjsValue) });
  });
};

const addArrayUpdates = (updates: FormUpdate[], event: Y.YArrayEvent<any>) => {
  const path = getFieldPath(event.path);
  if (!path) return;

  let cursor = 0;

  event.changes.delta.forEach(({ insert, delete: deleteCount, retain }) => {
    if (retain) cursor += retain;

    if (insert && Array.isArray(insert)) {
      insert.forEach((value, index) => {
        updates.push({
          type: 'insert',
          path,
          index: cursor + index,
          value: getYjsValue(value),
        });
      });

      cursor += insert.length;
    }

    if (deleteCount) {
      for (let index = 0; index < deleteCount; index++) {
        updates.push({ type: 'delete', path, index: cursor });
      }
    }
  });
};

const getFieldUpdates = (events: Y.YEvent<any>[], valsMap: Y.Map<any>) => {
  const updates: FormUpdate[] = [];

  events.forEach((event) => {
    if (event instanceof Y.YTextEvent) {
      updates.push({
        type: 'set',
        path: getFieldPath(event.path),
        value: event.target.toJSON(),
      });
      return;
    }

    if (event instanceof Y.YMapEvent) {
      addMapUpdates(updates, event, valsMap);
      return;
    }

    if (event instanceof Y.YArrayEvent) {
      addArrayUpdates(updates, event);
    }
  });

  return updates;
};

const updateFormFieldsFromYjs = (
  form: ObservableForm,
  updates: FormUpdate[],
) => {
  batch(() => {
    updates.forEach((update) => {
      const { path } = update;

      if (update.type === 'insert') {
        form.insertFieldValue(path, update.index, update.value);
        return;
      }

      if (update.type === 'delete') {
        form.removeFieldValue(path, update.index);
        return;
      }

      form.setFieldValue(path, update.value);
    });
  });
};

const useYjsFormObserver = (
  form: ObservableForm,
  yjsDocument?: Y.Doc | null,
) => {
  useEffect(() => {
    if (!yjsDocument) return;

    const valsMap = yjsDocument.getMap('vals');

    const observeFn = (events: Y.YEvent<any>[], transaction: Y.Transaction) => {
      if (transaction.local) return;

      const updates = getFieldUpdates(events, valsMap);
      if (!updates.length) return;

      updateFormFieldsFromYjs(form, updates);
    };

    valsMap.observeDeep(observeFn);

    return () => valsMap.unobserveDeep(observeFn);
  }, [form, yjsDocument]);
};

export default useYjsFormObserver;
